/**
 * RevolutionSelectbox — custom dropdown/selectbox widget.
 * <localization-selectors> — custom element wrapping market/language selectors.
 *
 * Extracted from global.js. Loaded as ES module via importmap alias
 * `@theme/selectbox`. Exposes `window.RevolutionSelectbox` so classic
 * consumers in `global.js` (ProductElement etc.) can still use it.
 *
 * Dependencies:
 *   - @theme/delegate, @theme/responsive, @theme/body-scroll-lock-options
 *   - @theme/focus-trap
 *   - @theme/body-scroll-lock
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';
import { disableBodyScroll, clearAllBodyScrollLocks } from '@theme/body-scroll-lock';

export class RevolutionSelectbox {
  constructor(element, popover, additionalOverlay) {
    this.element = element;
    this.popover = popover;
    this.changeOverlay = additionalOverlay;
    this.domDelegate = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);
    this.dropdown = this.element.querySelector('.RevolutionSelectbox');
    this.isOpen = false;
    this.mainDropdown = this.element.querySelector('.RevolutionSelectbox--Content');
    this.mainDropdownContent = this.element.querySelector('.RevolutionSelectbox--InnerContent');
    this.mainButton = this.element.querySelector('.RevolutionSelectbox--Button');

    this.connectedSelect = this.element.getAttribute('data-select');
    this.selectbox = this.element.querySelector('[name=' + this.connectedSelect + ']');
    if (this.selectbox == null) {
      this.selectbox = this.element.querySelector('[name2=' + this.connectedSelect + ']');
    }

    if (this.popover == true) {
      this.mainDropdown.classList.remove('RevolutionSelectbox--animation');
      this.mainDropdown.classList.add('PopoverContent');
      if (this.changeOverlay) {
        this.pageOverlayElement = document.querySelector('.localizationOverlay');
      } else {
        this.pageOverlayElement = document.querySelector('.page-overlay');
      }
    }

    this.bindEvents();
    this.initSelectbox();
  }

  bindEvents() {
    this.onClickOutsideListener = this.onClickOutside.bind(this);
    this.domDelegate.on('click', '[data-action=toggle-selectbox]', this.toggleDropdown.bind(this));
    this.domDelegate.on('click', '[data-action=selectbox-submit]', this.selectboxSubmit.bind(this));
    this.domDelegate.on(
      'keypress',
      '[data-action=selectbox-submit]',
      this.selectboxSubmit.bind(this)
    );
    this.domDelegate.on('click', '[data-action=selectbox-change]', this.selectboxChange.bind(this));

    this.delegateBody.on('menu:open', this.onClickOutside.bind(this));
    this.domDelegate.on('click', 'body', this.onClickOutside.bind(this));
  }

  selectboxChange() {
    var value = event.target.getAttribute('data-value');
    var selectboxID = event.target.getAttribute('data-selectboxid');
    var innerContent = event.target.innerHTML;
    var selEl = this.element.querySelector('#' + selectboxID);
    if (selEl.nodeName == 'SELECT') {
      selEl.value = value;
      selEl.dispatchEvent(new Event('change'));
    } else {
      selEl.querySelector('[value="' + value + '"]').setAttribute('checked', 'true');
    }
    this.mainButton.querySelector('.RevolutionSelectbox--Title').innerHTML = innerContent;

    if (this.element.classList.contains('dropdownSelector')) {
      this.element
        .querySelector('.RevolutionSelectbox--Item[disabled="true"]')
        .removeAttribute('disabled');
      event.target.setAttribute('disabled', 'true');
    }
    this.closeDropdown(this.element);
  }

  onClickOutside(event) {
    const withinBoundaries = event.composedPath().includes(this.element);
    if (!withinBoundaries || event.target.classList.contains('FiltersDesktopWrapper')) {
      this.closeDropdown(this.element);
    }
  }

  selectboxSubmit(event) {
    if (event.key != 'Enter' && event.key !== undefined) {
      return;
    }
    var value = event.target.getAttribute('data-value');
    var dataName = event.target.getAttribute('data-name');
    this.selectbox.value = value;
    this.closeDropdown(this.element);
    this.mainButton.querySelector('.RevolutionSelectbox--Title').innerHTML = dataName;
    this.selectbox.parentElement.submit();
  }

  initSelectbox() {
    const selectedItem = this.mainDropdown.querySelector('[selected="selected"]');
    const titleElement = this.element.querySelector('.RevolutionSelectbox--Title');
    if (selectedItem) {
      titleElement.innerHTML = selectedItem.getAttribute('data-name');
    }
    trapFocus(this.element);
    this.element.style.display = 'block';
  }

  toggleDropdown(event, target) {
    event.preventDefault();
    if (this.isOpen) {
      this.closeDropdown(target);
    } else {
      this.openDropdown(target);
    }
  }

  openDropdown(target) {
    this.element.dispatchEvent(new CustomEvent('close:search', { bubbles: true }));
    this.element.dispatchEvent(new CustomEvent('menu:close', { bubbles: true }));

    const siblings = Array.from(target.parentElement.parentElement.children).filter((item) =>
      item.classList.contains('RevolutionSelectbox')
    );

    siblings.forEach((item) => {
      item.querySelector('.RevolutionSelectbox--Button').setAttribute('aria-expanded', false);
      item.querySelector('.RevolutionSelectbox--Content').setAttribute('aria-hidden', true);
      item.querySelector('.RevolutionSelectbox--Content').style.display = 'none';
    });

    if (this.popover) {
      this.pageOverlayElement.classList.add('is-visible');
      this.pageOverlayElement.addEventListener('click', this.onClickOutsideListener);
      const focus = this.element.querySelector('[data-scrollable]');
      if (typeof bodyScrollLock !== 'undefined') {
        disableBodyScroll(focus, bodyScrollLockOptions);
      }
      document.body.classList.add('scrollLock');
      trapFocus(this.mainDropdown);
      const drawer = document.querySelector('#Drawer');
      if (drawer && drawer.getAttribute('aria-expanded') === 'true') {
        document.querySelector('.Drawer--Wrapper').style.overflow = 'visible';
      }
    }

    const contentElement = target.parentElement.querySelector('.RevolutionSelectbox--Content');
    target.setAttribute('aria-expanded', 'true');
    contentElement.style.display = 'block';
    contentElement.style.height = `${contentElement.offsetHeight}px`;

    if (this.popover) {
      setTimeout(() => {
        contentElement.setAttribute('aria-hidden', 'false');
      }, 20);
    } else {
      contentElement.setAttribute('aria-hidden', 'false');
    }

    this.isOpen = true;
    window.addEventListener('click', this.onClickOutsideListener);
  }

  closeDropdown(target) {
    let closestDropdown = target.closest('.RevolutionSelectbox');
    if (closestDropdown === null) {
      closestDropdown = target.querySelector("[aria-expanded='true']").parentElement;
    }
    closestDropdown
      .querySelector('.RevolutionSelectbox--Button')
      .setAttribute('aria-expanded', false);
    closestDropdown
      .querySelector('.RevolutionSelectbox--Content')
      .setAttribute('aria-hidden', true);
    setTimeout(() => {
      closestDropdown.querySelector('.RevolutionSelectbox--Content').style.display = 'none';
    }, 300);

    if (this.popover == true) {
      this.pageOverlayElement.classList.remove('is-visible');
      this.pageOverlayElement.removeEventListener('click', this.onClickOutsideListener);

      const menuDrawer = document.querySelector('#Drawer');
      if (menuDrawer && menuDrawer.getAttribute('aria-expanded') == 'true') {
        document.querySelector('.Drawer--Wrapper').style.overflow = 'auto';
      } else if (typeof bodyScrollLock !== 'undefined') {
        clearAllBodyScrollLocks();
        document.body.classList.remove('scrollLock');
      }
    }

    this.isOpen = false;
    removeTrapFocus(this.mainDropdownContent);
    window.removeEventListener('click', this.onClickOutsideListener);
  }
}

// Bridge for classic-script consumers (global.js ProductElement). Removed in Faza 5.
window.RevolutionSelectbox = RevolutionSelectbox;

class LocalizationSelectors extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    const breakpoint = responsive.getCurrentBreakpoint();
    const selectBoxes = this.element.querySelectorAll('.RevolutionSelectbox');
    const location = this.element.getAttribute('data-settings');
    const additionalOverlay = location === 'sidebarMenu';

    if (breakpoint !== 'phone' && breakpoint !== 'tablet') {
      selectBoxes.forEach((item) => {
        new RevolutionSelectbox(item);
      });
    } else {
      selectBoxes.forEach((item) => {
        new RevolutionSelectbox(item, true, additionalOverlay);
      });
    }
  }
}

if (!customElements.get('localization-selectors')) {
  customElements.define('localization-selectors', LocalizationSelectors);
}
